import { AnnouncementSectionProps } from '../validators/AnnouncementValidators';
import { Box, FormControl, FormHelperText, Grid, MenuItem, Select, SelectChangeEvent, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { AnnouncementFrequencies, ScheduleDayOffset } from '@inosio/payments-sdk';
import { convertToHumanReadable } from '../../../utils/string-util';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { AnnouncementState, selectAnnouncementsState } from '../sagas/announcementSlice';
import { useSelector } from 'react-redux';
import { parseISO } from 'date-fns';
import HelpIcon from '@mui/icons-material/Help';
import { allMonths, generateRecurringAnnouncementSentence, ScheduleDayOffsetOptions } from './utils/utils';
import { useEffect } from 'react';

const schemaValidation = Yup.object({
  Frequency: Yup.string().required('Frequency is required'),
  FirstMonth: Yup.number().nullable().required('First month is required'),
  SendIt: Yup.string().nullable(),
  Date: Yup.number().nullable().required('Date is required'),
  Time: Yup.date().nullable().required('Time is required'),
});

type schemaValidationType = Omit<Yup.InferType<typeof schemaValidation>, 'Time'> & {
  Time: Date | null;
};

interface IProps extends AnnouncementSectionProps {}

const datesOptions = Array.from({ length: 31 }, (_, index) => index + 1);

export const RecurringSchedule = (props: IProps) => {
  const { announcementDetail }: AnnouncementState = useSelector(selectAnnouncementsState);

  const formik = useFormik<schemaValidationType>({
    initialValues: {
      Frequency: announcementDetail.Frequency || AnnouncementFrequencies.Monthly,
      FirstMonth: announcementDetail.FirstSendDatetime ? parseISO(announcementDetail.FirstSendDatetime).getMonth() : new Date().getMonth(),
      SendIt: announcementDetail.ScheduleDayOffset || ScheduleDayOffsetOptions[0].value,
      Date: announcementDetail.FirstSendDatetime ? parseISO(announcementDetail.FirstSendDatetime).getDate() : new Date().getDate(),
      Time: announcementDetail.FirstSendDatetime ? parseISO(announcementDetail.FirstSendDatetime) : new Date(new Date().setHours(0, 0, 0, 0)),
    },
    validationSchema: schemaValidation,
    onSubmit: (values) => {},
  });

  const getError = (fieldName: keyof schemaValidationType) => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? <Typography sx={{ fontSize: '12px', color: '#d32f2f', fontFamily: 'Work Sans' }}>{formik.errors[fieldName]}</Typography> : <></>;
  };

  const handleSendOffsetChange = (event: SelectChangeEvent<any>) => {
    const scheduleDayOffset = event.target.value as ScheduleDayOffset;
    formik.setFieldValue('SendIt', scheduleDayOffset).then((error) => {
      props.setPendingAnnouncement({
        ScheduleDayOffset: scheduleDayOffset,
      });
    });
  };

  const onHandleFrequencyChange = (event: SelectChangeEvent<any>) => {
    const frequency = event.target.value as AnnouncementFrequencies;
    formik.setFieldValue('Frequency', frequency).then((error) => {
      const firstSendDate = calculateFirstSendDate(formik.values.FirstMonth, formik.values.Date, formik.values.Time);
      if (firstSendDate) {
        props.setPendingAnnouncement({
          Frequency: frequency,
        });
      }
    });
  };

  const onHandleMonthChange = (event: SelectChangeEvent<any>) => {
    const selectedMonth = event.target.value;
    formik.setFieldValue('FirstMonth', selectedMonth).then((error) => {
      const firstSendDate = calculateFirstSendDate(selectedMonth, formik.values.Date, formik.values.Time);
      updateFirstSendDate(firstSendDate);
    });
  };

  const onHandleDateChange = (event: SelectChangeEvent<any>) => {
    const selectedDate = event.target.value;
    formik.setFieldValue('Date', selectedDate).then((error) => {
      const firstSendDate = calculateFirstSendDate(formik.values.FirstMonth, selectedDate, formik.values.Time);
      updateFirstSendDate(firstSendDate);
    });
  };

  const onHandleTimeChange = (time: Date | null) => {
    formik.setFieldValue('Time', time).then((error) => {
      const firstSendDate = calculateFirstSendDate(formik.values.FirstMonth, formik.values.Date, time);
      updateFirstSendDate(firstSendDate);
    });
  };

  const updateFirstSendDate = (firstSendDateTime: Date | null) => {
    if (firstSendDateTime) {
      props.setPendingAnnouncement({ FirstSendDatetime: firstSendDateTime.toISOString() });
    }
  };

  const calculateFirstSendDate = (firstMonth: number | null, date: number | null, time: Date | null) => {
    if (firstMonth && date && time) {
      const currentYear = new Date().getFullYear();
      const firstSendDate = new Date(currentYear, firstMonth, date);
      firstSendDate.setHours(time.getHours());
      firstSendDate.setMinutes(time.getMinutes());
      firstSendDate.setSeconds(0);
      firstSendDate.setMilliseconds(0);

      return firstSendDate;
    }
    return null;
  };

  useEffect(() => {
    if ((!announcementDetail.Frequency || announcementDetail.Frequency === AnnouncementFrequencies.OneTime) && announcementDetail.FirstSendDatetime) {
      const frequency = AnnouncementFrequencies.Monthly;
      formik.setFieldValue('Frequency', frequency).then((possibleErrors) => {
        if (!possibleErrors?.Frequency) {
          props.setPendingAnnouncement({
            Frequency: frequency,
          });
        }
      });
    }
  }, [announcementDetail]);

  useEffect(() => {
    if (!announcementDetail.FirstSendDatetime) {
      const firstSendDate = calculateFirstSendDate(formik.values.FirstMonth, formik.values.Date, formik.values.Time);
      props.setPendingAnnouncement({
        Frequency: formik.values.Frequency as AnnouncementFrequencies,
        ScheduleDayOffset: formik.values.SendIt as ScheduleDayOffset,
        FirstSendDatetime: firstSendDate?.toISOString(),
      });
    }
  }, [announcementDetail]);

  const textBaseStyle: SxProps<Theme> = {
    color: '#000000',
    fontFamily: 'Work Sans',
    lineHeight: '22px',
    fontSize: '18px',
  };

  const labelStyle: SxProps<Theme> = {
    fontFamily: 'Work Sans',
    fontWeight: 500,
    marginTop: '-15px',
    textAlign: 'right',
    width: '100px',
    display: 'flex',
    justifyContent: 'flex-end',
  };

  const sxStyle = {
    borderRadius: '24px',
    minWidth: '320px',
    '& .MuiInputBase-root': {
      height: '40px',
      borderRadius: '24px',
      '& input': {
        height: '40px',
        padding: '0 14px',
        lineHeight: '40px',
      },
    },
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            sx={{
              ...textBaseStyle,
              fontWeight: '600',
            }}
          >
            Select Recurring Schedule Settings:
          </Typography>
        </Grid>

        <Grid container mt={'30px'} rowSpacing={1} sx={{ width: '100%', height: '100%' }}>
          {/* Frequency Field */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={labelStyle}>Frequency:</Typography>
            <FormControl sx={{ width: '100%', maxWidth: '175px', borderRadius: 24, height: 50 }} size="small">
              <Select
                sx={{ borderRadius: '24px', height: '37px' }}
                error={!!formik.errors.Frequency && formik.touched.Frequency}
                {...formik.getFieldProps('Frequency')}
                fullWidth
                displayEmpty
                placeholder="Select Frequency"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                      marginTop: '10px',
                    },
                  },
                }}
                onChange={onHandleFrequencyChange}
              >
                <MenuItem disabled value="">
                  Select Frequency
                </MenuItem>
                <MenuItem value={AnnouncementFrequencies.Monthly}>{convertToHumanReadable(AnnouncementFrequencies.Monthly)}</MenuItem>
                <MenuItem value={AnnouncementFrequencies.Quarterly}>{convertToHumanReadable(AnnouncementFrequencies.Quarterly)}</MenuItem>
              </Select>
              {getError('Frequency')}
            </FormControl>
          </Grid>

          {/* First Month Field */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={labelStyle}>First Month:</Typography>
            <FormControl sx={{ width: '100%', maxWidth: '175px', borderRadius: 24, height: 50 }} size="small">
              <Select
                sx={{ borderRadius: '24px', height: '37px' }}
                error={!!formik.errors.FirstMonth && formik.touched.FirstMonth}
                {...formik.getFieldProps('FirstMonth')}
                fullWidth
                displayEmpty
                onChange={onHandleMonthChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                      marginTop: '10px',
                    },
                  },
                }}
              >
                <MenuItem disabled value={''}>
                  Select Month
                </MenuItem>
                {allMonths.map((month) => (
                  <MenuItem key={month.Id} value={month.Id}>
                    {month.Name}
                  </MenuItem>
                ))}
              </Select>
              {getError('FirstMonth')}
            </FormControl>
          </Grid>

          {/* Send it Field */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={labelStyle}>
              Send it:
              <Tooltip title="Weekends and federal holidays are excluded from business days." placement="bottom">
                <HelpIcon sx={{ marginTop: '5px', fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
              </Tooltip>
            </Typography>

            <FormControl sx={{ width: '100%', maxWidth: '415px', borderRadius: 24, height: 50 }} size="small">
              <Select
                sx={{ borderRadius: '24px', height: '37px' }}
                error={!!formik.errors.SendIt && formik.touched.SendIt}
                {...formik.getFieldProps('SendIt')}
                fullWidth
                displayEmpty
                onChange={handleSendOffsetChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                      marginTop: '10px',
                    },
                  },
                }}
              >
                <MenuItem disabled value={''}>
                  Select Offset
                </MenuItem>
                {ScheduleDayOffsetOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {getError('SendIt')}
            </FormControl>
          </Grid>

          {/* Date Field */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={labelStyle}>
              Date:
              <Tooltip title="If selecting 29-31, the last day of the month will be used during months that do not have that many days." placement="bottom">
                <HelpIcon sx={{ marginTop: '5px', fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
              </Tooltip>
            </Typography>
            <FormControl sx={{ width: '100%', maxWidth: '175px', borderRadius: 24, height: 50 }} size="small">
              <Select
                sx={{ borderRadius: '24px', height: '37px' }}
                error={!!formik.errors.Date && formik.touched.Date}
                {...formik.getFieldProps('Date')}
                fullWidth
                displayEmpty
                onChange={onHandleDateChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                      marginTop: '10px',
                    },
                  },
                }}
              >
                <MenuItem disabled value={''}>
                  Select Date
                </MenuItem>
                {datesOptions.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </Select>
              {getError('Date')}
            </FormControl>
          </Grid>

          {/* Time Field */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={{ ...labelStyle, marginTop: '-3px' }}>Time:</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                value={formik.values.Time ? new Date(formik.values.Time) : null}
                sx={{
                  ...sxStyle,
                  width: '175px',
                  minWidth: 'unset',
                }}
                disablePast={false}
                timeSteps={{ hours: 1, minutes: 30 }}
                onAccept={onHandleTimeChange}
                slotProps={{
                  textField: {
                    onKeyDown: (e: any) => {
                      e.preventDefault();
                    },
                    onPaste: (e: any) => {
                      e.preventDefault();
                    },
                  },
                  popper: {
                    sx: {
                      '.MuiPaper-root': {
                        borderRadius: '24px',
                        marginTop: '10px',
                        paddingTop: '10px',
                        width: '175px',
                        '.MuiMultiSectionDigitalClock-root': {
                          width: '175px',
                          justifyContent: 'space-evenly',
                        },
                        '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                          backgroundColor: '#39A2DB',
                        },
                        '.MuiMultiSectionDigitalClockSection-root': {
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        },
                      },
                    },
                  },
                }}
              />
              {formik.errors.Time && formik.touched.Time && <FormHelperText error>{formik.errors.Time}</FormHelperText>}
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 600, marginTop: '50px', width: '60%' }}>{generateRecurringAnnouncementSentence(announcementDetail)}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
